package com.arena_web.utils

import kotlinx.browser.window

object LeafletMap {

    /**
     * Create an interactive address-picker map with a FIXED centre pin.
     * The pin stays pinned to the centre; the user pans the map underneath it to
     * position it, and pinch / scroll / +- buttons zoom for precision. Each
     * pan/zoom fires the move callback with the map's centre coordinates.
     */
    fun init(containerId: String, lat: Double, lng: Double, zoom: Int = 13) {
        window.asDynamic().__arenaMapContainerId = containerId
        window.asDynamic().__arenaMapLat = lat
        window.asDynamic().__arenaMapLng = lng
        window.asDynamic().__arenaMapZoom = zoom

        js("""
            (function() {
                var id = window.__arenaMapContainerId;
                var lat = window.__arenaMapLat;
                var lng = window.__arenaMapLng;
                var zoom = window.__arenaMapZoom;
                var container = document.getElementById(id);
                if (!container) { console.warn('[LeafletMap] container not found:', id); return; }
                if (typeof L === 'undefined') { console.warn('[LeafletMap] Leaflet not loaded'); return; }

                if (window.__arenaMap) {
                    try { window.__arenaMap.remove(); } catch(e) {}
                    window.__arenaMap = null;
                }

                // Remove old pin if exists
                var oldPin = document.getElementById(id + '-pin');
                if (oldPin) oldPin.remove();

                container.style.position = 'relative';

                // Fixed centre pin — a CSS overlay (pointer-events:none) so the
                // map pans under it; the centre is the selected point.
                var pin = document.createElement('div');
                pin.id = id + '-pin';
                pin.style.cssText = 'position:absolute;top:50%;left:50%;z-index:999;' +
                    'transform:translate(-50%,-100%);pointer-events:none;' +
                    'width:30px;height:40px;filter:drop-shadow(0 3px 6px rgba(0,0,0,0.4));';
                pin.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="30" height="40">' +
                    '<path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z" fill="#0061FF"/>' +
                    '<circle cx="12" cy="11" r="5" fill="white"/>' +
                    '</svg>';
                container.appendChild(pin);

                // Keep wheel-zoom from scrolling the page. We deliberately do NOT
                // stop `touchmove`: Leaflet tracks drag moves via a document-level
                // listener, so stopping propagation here would block map panning on
                // touch. The host bottom sheet ignores map touches via its
                // `isInteractive` check (it treats `.leaflet-container` as a control).
                container.addEventListener('wheel', function(e) { e.stopPropagation(); }, { passive: false });

                var worldBounds = L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180));
                var map = L.map(id, {
                    zoomControl: true,
                    attributionControl: false,
                    scrollWheelZoom: true,
                    doubleClickZoom: true,
                    touchZoom: true,
                    boxZoom: true,
                    maxBounds: worldBounds,
                    maxBoundsViscosity: 1.0,
                    minZoom: 2,
                    worldCopyJump: true
                }).setView([lat, lng], zoom);

                L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                    maxZoom: 20,
                    subdomains: 'abcd',
                    noWrap: true,
                    bounds: worldBounds
                }).addTo(map);

                window.__arenaMap = map;
                window.__arenaMapSkipMove = false;

                // Pan / zoom moves the map under the fixed centre pin; report the
                // new centre as the selected point.
                map.on('moveend', function() {
                    if (window.__arenaMapSkipMove) {
                        window.__arenaMapSkipMove = false;
                        return;
                    }
                    var c = map.getCenter();
                    if (typeof window.__arenaMapMoveCallback === 'function') {
                        window.__arenaMapMoveCallback(c.lat, c.lng);
                    }
                });

                setTimeout(function() { map.invalidateSize(); }, 150);
            })();
        """)
    }

    /**
     * Center map on coordinates without triggering the move callback.
     * Used when autocomplete sets the location programmatically.
     */
    fun setView(lat: Double, lng: Double, zoom: Int = 15) {
        window.asDynamic().__arenaMapLat = lat
        window.asDynamic().__arenaMapLng = lng
        window.asDynamic().__arenaMapZoom = zoom

        js("""
            (function() {
                if (!window.__arenaMap) return;
                window.__arenaMapSkipMove = true;
                window.__arenaMap.setView(
                    [window.__arenaMapLat, window.__arenaMapLng],
                    window.__arenaMapZoom
                );
            })();
        """)
    }

    /**
     * Register a Kotlin callback for when the map center changes (user drag/zoom).
     */
    fun onMoveEnd(callback: (lat: Double, lng: Double) -> Unit) {
        window.asDynamic().__arenaMapMoveCallback = callback
    }

    /**
     * Create a read-only map for browsing markers (no center pin, no move callback).
     */
    fun initReadOnly(containerId: String, lat: Double, lng: Double, zoom: Int = 5) {
        window.asDynamic().__arenaMapContainerId = containerId
        window.asDynamic().__arenaMapLat = lat
        window.asDynamic().__arenaMapLng = lng
        window.asDynamic().__arenaMapZoom = zoom

        js("""
            (function() {
                var id = window.__arenaMapContainerId;
                var lat = window.__arenaMapLat;
                var lng = window.__arenaMapLng;
                var zoom = window.__arenaMapZoom;
                var container = document.getElementById(id);
                if (!container) { console.warn('[LeafletMap] container not found:', id); return; }
                if (typeof L === 'undefined') { console.warn('[LeafletMap] Leaflet not loaded'); return; }

                if (window.__arenaMap) {
                    try { window.__arenaMap.remove(); } catch(e) {}
                    window.__arenaMap = null;
                }

                container.style.position = 'relative';

                // Don't stop `touchmove` — Leaflet tracks drag/zoom via document
                // listeners, so stopping it here would block panning & pinch-zoom
                // (e.g. inside a bottom sheet). Wheel is kept so it doesn't scroll
                // the page.
                container.addEventListener('wheel', function(e) { e.stopPropagation(); }, { passive: false });

                var worldBounds = L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180));
                var map = L.map(id, {
                    zoomControl: true,
                    attributionControl: false,
                    scrollWheelZoom: true,
                    doubleClickZoom: true,
                    touchZoom: true,
                    boxZoom: true,
                    maxBounds: worldBounds,
                    maxBoundsViscosity: 1.0,
                    minZoom: 2,
                    worldCopyJump: true
                }).setView([lat, lng], zoom);

                L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                    maxZoom: 20,
                    subdomains: 'abcd',
                    noWrap: true,
                    bounds: worldBounds
                }).addTo(map);

                window.__arenaMap = map;
                window.__arenaMarkerGroup = L.layerGroup().addTo(map);

                // Zoomed-out maps shrink the info-card pins — a CSS variable
                // drives scale(var(--arena-pin-scale)) on .arena-pin-wrap.
                var applyPinScale = function() {
                    var z = map.getZoom();
                    var s = z >= 13 ? 1 : z >= 11.5 ? 0.85 : z >= 10 ? 0.7 : 0.55;
                    container.style.setProperty('--arena-pin-scale', s);
                };
                map.on('zoom zoomend', applyPinScale);
                applyPinScale();

                setTimeout(function() { map.invalidateSize(); }, 150);
            })();
        """)
    }

    /**
     * Add markers from a JSON array string:
     * `[{id?, lat, lng, title, subtitle?, image?, datetime?, members?, cost?}]`.
     * Each marker gets a dark mini-card popup (photo + info) styled to the app
     * theme (`.arena-map-popup` overrides in AppTheme). When `id` is present,
     * tapping the marker also fires [onMarkerTap] (the Arena map syncs its
     * card strip) and clicking the popup card fires [onMarkerOpen] (open
     * details intent).
     */
    fun addMarkers(jsonArray: String) {
        window.asDynamic().__arenaMarkersJson = jsonArray

        js("""
            (function() {
                var map = window.__arenaMap;
                var group = window.__arenaMarkerGroup;
                if (!map || !group) return;

                // Titles/addresses are user input — escape before injecting HTML.
                var esc = function(s) {
                    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
                };

                var items = JSON.parse(window.__arenaMarkersJson);
                var bounds = [];

                items.forEach(function(item) {
                    var lat = parseFloat(item.lat);
                    var lng = parseFloat(item.lng);
                    if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return;

                    // Event items (datetime/image present) render as info-card
                    // pins — photo + date/time + title always visible on the
                    // map, no popup needed (.arena-pin-* styles in AppTheme).
                    if (item.datetime || item.image) {
                        // Photo IS the card — scrim + text over it; the scrim
                        // and (optional) photo share one background stack.
                        var bg = 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 45%, rgba(0,0,0,0.8) 100%)';
                        if (item.image) {
                            bg += ', url(&quot;' + esc(item.image) + '&quot;)';
                        }
                        var pinHtml = '<div class="arena-pin-wrap">' +
                            '<div class="arena-pin-card" style="background-image:' + bg + '">' +
                            (item.datetime ? '<div class="arena-pin-datetime">' + esc(item.datetime) + '</div>' : '') +
                            (item.title ? '<div class="arena-pin-title">' + esc(item.title) + '</div>' : '') +
                            '</div>' +
                            '<div class="arena-pin-tip"></div>' +
                            '</div>';
                        var cardMarker = L.marker([lat, lng], {
                            icon: L.divIcon({ className: 'arena-pin', html: pinHtml, iconSize: [0, 0], iconAnchor: [0, 0] })
                        });
                        if (item.id) {
                            cardMarker.on('click', function() {
                                if (typeof window.__arenaMarkerTapCallback === 'function') {
                                    window.__arenaMarkerTapCallback(String(item.id));
                                }
                            });
                        }
                        group.addLayer(cardMarker);
                        bounds.push([lat, lng]);
                        return;
                    }

                    var marker = L.marker([lat, lng]);

                    // Mini card popup — photo on top, then title / subtitle
                    // (clubs and other id-less markers).
                    var html = '<div style="min-width:200px;max-width:240px">';
                    if (item.image) {
                        html += '<img src="' + esc(item.image) + '" style="display:block;width:100%;height:108px;object-fit:cover" />';
                    }
                    html += '<div style="padding:10px 12px 12px;font-family:inherit">';
                    if (item.datetime) {
                        html += '<div style="font-size:12px;font-weight:600;color:#ffffff;letter-spacing:0.2px">' + esc(item.datetime) + '</div>';
                    }
                    if (item.title) {
                        html += '<div style="font-size:14px;font-weight:600;color:#ffffff;margin-top:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + esc(item.title) + '</div>';
                    }
                    if (item.subtitle) {
                        html += '<div style="font-size:12px;color:#8aa0c8;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + esc(item.subtitle) + '</div>';
                    }
                    var meta = [];
                    if (item.members) meta.push(esc(item.members));
                    if (item.cost) meta.push(esc(item.cost));
                    if (meta.length > 0) {
                        html += '<div style="font-size:12px;font-weight:500;color:#b4ccf6;margin-top:8px">' + meta.join(' &middot; ') + '</div>';
                    }
                    html += '</div></div>';

                    var popupHtml;
                    if (item.id) {
                        // Whole card is the "open details" affordance.
                        popupHtml = '<div style="cursor:pointer" onclick="window.__arenaMarkerOpenCallback && window.__arenaMarkerOpenCallback(\'' + esc(String(item.id)) + '\')">' + html + '</div>';
                    } else {
                        popupHtml = html;
                    }
                    marker.bindPopup(popupHtml, {
                        maxWidth: 260,
                        closeButton: false,
                        className: 'arena-map-popup',
                        offset: L.point(0, -6)
                    });
                    if (item.id) {
                        marker.on('click', function() {
                            if (typeof window.__arenaMarkerTapCallback === 'function') {
                                window.__arenaMarkerTapCallback(String(item.id));
                            }
                        });
                    }
                    group.addLayer(marker);
                    bounds.push([lat, lng]);
                });

                if (bounds.length > 0) {
                    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
                }
            })();
        """)
    }

    /** Marker tap (receives the marker's `id`) — only markers created with an id fire it. */
    fun onMarkerTap(callback: (String) -> Unit) {
        window.asDynamic().__arenaMarkerTapCallback = callback
    }

    /** Popup mini-card click (receives the marker's `id`) — "open details" intent. */
    fun onMarkerOpen(callback: (String) -> Unit) {
        window.asDynamic().__arenaMarkerOpenCallback = callback
    }

    /**
     * Remove all markers from the map.
     */
    fun clearMarkers() {
        js("""
            (function() {
                if (window.__arenaMarkerGroup) {
                    window.__arenaMarkerGroup.clearLayers();
                }
            })();
        """)
    }

    fun destroy() {
        js("""
            (function() {
                if (window.__arenaMarkerGroup) {
                    try { window.__arenaMarkerGroup.clearLayers(); } catch(e) {}
                    window.__arenaMarkerGroup = null;
                }
                if (window.__arenaMap) {
                    try { window.__arenaMap.remove(); } catch(e) {}
                    window.__arenaMap = null;
                }
                window.__arenaMapMoveCallback = null;
                window.__arenaMarkerTapCallback = null;
                window.__arenaMarkerOpenCallback = null;
                window.__arenaMapSkipMove = false;
            })();
        """)
    }
}
